import { html, css, PropertyValues } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { EwcBase } from '../base/ewc-base';
import { FormValidation, emit, waitForEvent, a11y } from '../../adapters/webawesome-adapter';

// Apply WebAwesome form validation mixin
const EwcSelectBase = FormValidation(EwcBase);

@customElement('ewc-select')
export class EwcSelect extends EwcSelectBase {
  @query('.select-trigger') trigger!: HTMLButtonElement;
  @query('.select-dropdown') dropdown!: HTMLDivElement;
  @query('.select-search') searchInput!: HTMLInputElement;
  
  @property({ type: String }) placeholder = 'Select an option';
  @property({ type: String }) label = '';
  @property({ type: String }) error = '';
  @property({ type: String }) helper = '';
  @property({ type: Boolean }) multiple = false;
  @property({ type: Boolean }) searchable = false;
  @property({ type: Boolean }) clearable = false;
  @property({ type: Number, attribute: 'max-height' }) maxHeight = 300;
  @property({ type: Boolean }) pill = false;
  @property({ type: String }) appearance: 'outlined' | 'filled' = 'outlined';
  @property({ type: Boolean }) open = false;
  @property({ type: Number, attribute: 'max-options-visible' }) maxOptionsVisible = 3;
  
  @state() private _selectedOptions: string[] = [];
  @state() private _searchQuery = '';
  @state() private _filteredOptions: Array<{value: string, label: string}> = [];
  @state() private _highlightedIndex = -1;
  
  @property({ type: Array })
  get options(): Array<{value: string, label: string}> {
    return this._options;
  }
  set options(val: Array<{value: string, label: string}>) {
    this._options = val;
    this._filteredOptions = val;
  }
  private _options: Array<{value: string, label: string}> = [];

  static override get styles() {
    return [
      ...EwcSelectBase.styles,
      css`
        :host {
          display: block;
          width: 100%;
          position: relative;
        }

        .select-wrapper {
          position: relative;
          width: 100%;
        }

        .label {
          display: block;
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--ewc-text-color-default);
        }

        .select-trigger {
          width: 100%;
          min-height: var(--ewc-height);
          padding: var(--ewc-padding-y) var(--ewc-padding-x);
          padding-right: 2.5rem;
          font-size: var(--ewc-font-size);
          font-family: inherit;
          border: 2px solid var(--ewc-border-color);
          border-radius: var(--ewc-radius);
          background-color: var(--ewc-bg-color);
          color: var(--ewc-text-color-default);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
          transition: all 0.2s;
          text-align: left;
        }

        :host([pill]) .select-trigger {
          border-radius: var(--ewc-radius-full);
        }

        :host([appearance="filled"]) .select-trigger {
          background-color: var(--ewc-bg-secondary);
          border-color: transparent;
        }

        :host([appearance="filled"]) .select-trigger:focus {
          background-color: var(--ewc-bg-color);
          border-color: var(--ewc-color);
        }

        .select-trigger:hover:not(:disabled) {
          border-color: var(--ewc-color);
        }

        .select-trigger:focus {
          outline: none;
          border-color: var(--ewc-color);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .select-trigger:disabled {
          background-color: var(--ewc-bg-tertiary);
          cursor: not-allowed;
          opacity: 0.5;
        }

        /* Error state */
        :host([error]) .select-trigger {
          border-color: var(--ewc-color-error, #ef4444);
        }

        :host([error]) .select-trigger:focus {
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }

        .select-value {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .select-value.placeholder {
          color: var(--ewc-text-color-tertiary);
        }

        /* Multiple select tags */
        .select-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.25rem;
          flex: 1;
        }

        .select-tag {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.125rem 0.5rem;
          font-size: 0.75rem;
          background-color: var(--ewc-color-100);
          color: var(--ewc-color-800);
          border-radius: var(--ewc-radius-sm);
        }

        :host([pill]) .select-tag {
          border-radius: var(--ewc-radius-full);
        }

        .select-tag-remove {
          cursor: pointer;
          padding: 0;
          background: none;
          border: none;
          color: inherit;
          font-size: 1rem;
          line-height: 1;
          opacity: 0.7;
          transition: opacity 0.2s;
        }

        .select-tag-remove:hover {
          opacity: 1;
        }

        .select-tag-more {
          padding: 0.125rem 0.5rem;
          font-size: 0.75rem;
          color: var(--ewc-text-color-secondary);
        }

        /* Icons */
        .select-icons {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          position: absolute;
          right: var(--ewc-padding-x);
          top: 50%;
          transform: translateY(-50%);
        }

        .select-clear,
        .select-chevron {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 1.25rem;
          height: 1.25rem;
          color: var(--ewc-text-color-secondary);
          transition: all 0.2s;
        }

        .select-clear {
          cursor: pointer;
          border-radius: var(--ewc-radius-sm);
        }

        .select-clear:hover {
          background-color: rgba(0, 0, 0, 0.05);
          color: var(--ewc-text-color-default);
        }

        :host([theme="dark"]) .select-clear:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }

        .select-chevron {
          pointer-events: none;
        }

        .select-trigger:focus .select-chevron,
        :host([open]) .select-chevron {
          transform: rotate(180deg);
        }

        /* Dropdown */
        .select-dropdown {
          position: absolute;
          top: calc(100% + 0.25rem);
          left: 0;
          right: 0;
          z-index: var(--ewc-z-dropdown);
          background-color: var(--ewc-bg-color);
          border: 1px solid var(--ewc-border-color);
          border-radius: var(--ewc-radius);
          box-shadow: var(--ewc-shadow-lg);
          max-height: var(--max-height, 300px);
          overflow: hidden;
          opacity: 0;
          transform: translateY(-0.5rem);
          pointer-events: none;
          transition: all 0.2s;
        }

        :host([open]) .select-dropdown {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }

        /* Search */
        .select-search-wrapper {
          padding: 0.5rem;
          border-bottom: 1px solid var(--ewc-border-color);
        }

        .select-search {
          width: 100%;
          padding: 0.375rem 0.75rem;
          font-size: 0.875rem;
          border: 1px solid var(--ewc-border-color);
          border-radius: var(--ewc-radius-sm);
          background-color: var(--ewc-bg-color);
          color: var(--ewc-text-color-default);
        }

        .select-search:focus {
          outline: none;
          border-color: var(--ewc-color);
        }

        /* Options */
        .select-options {
          max-height: calc(var(--max-height, 300px) - 3rem);
          overflow-y: auto;
          padding: 0.25rem 0;
        }

        .select-option {
          padding: 0.5rem 1rem;
          cursor: pointer;
          transition: background-color 0.1s;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .select-option:hover,
        .select-option.highlighted {
          background-color: var(--ewc-hover-bg);
        }

        .select-option.selected {
          background-color: var(--ewc-color-50);
          color: var(--ewc-color-700);
          font-weight: 500;
        }

        .select-option.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .select-option-checkbox {
          width: 1rem;
          height: 1rem;
          border: 2px solid var(--ewc-border-color);
          border-radius: var(--ewc-radius-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .select-option.selected .select-option-checkbox {
          background-color: var(--ewc-color);
          border-color: var(--ewc-color);
        }

        .select-option.selected .select-option-checkbox::after {
          content: '✓';
          color: white;
          font-size: 0.75rem;
        }

        /* Empty state */
        .select-empty {
          padding: 2rem 1rem;
          text-align: center;
          color: var(--ewc-text-color-secondary);
          font-size: 0.875rem;
        }

        /* Helper text */
        .helper-text,
        .error-text {
          margin-top: 0.25rem;
          font-size: 0.75rem;
          color: var(--ewc-text-color-secondary);
        }

        .error-text {
          color: var(--ewc-color-error, #ef4444);
        }
      `
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    this._handleClickOutside = this._handleClickOutside.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
  }

  protected firstUpdated() {
    this._updateAccessibility();
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    if (changedProperties.has('value')) {
      this._updateSelectedOptions();
      emit(this, 'ewc-input', { detail: { value: this.value } });
    }

    if (changedProperties.has('open')) {
      if (this.open) {
        this._addEventListeners();
        emit(this, 'ewc-show');
        this._focusSearch();
      } else {
        this._removeEventListeners();
        emit(this, 'ewc-hide');
        this._searchQuery = '';
        this._filteredOptions = this.options;
      }
    }

    if (changedProperties.has('disabled') || changedProperties.has('required')) {
      this._updateAccessibility();
    }
  }

  private _updateAccessibility() {
    if (this.trigger) {
      a11y.setAttributes(this.trigger, {
        'aria-expanded': this.open,
        'aria-haspopup': 'listbox',
        'aria-invalid': this.error || !this.validity.valid,
        'aria-required': this.required,
        'aria-disabled': this.disabled,
        'aria-label': this.label || this.placeholder
      });
    }
  }

  private _updateSelectedOptions() {
    const value = this.value;
    if (this.multiple && Array.isArray(value)) {
      this._selectedOptions = value;
    } else if (value) {
      this._selectedOptions = [String(value)];
    } else {
      this._selectedOptions = [];
    }
  }

  private _addEventListeners() {
    document.addEventListener('click', this._handleClickOutside);
    document.addEventListener('keydown', this._handleKeyDown);
  }

  private _removeEventListeners() {
    document.removeEventListener('click', this._handleClickOutside);
    document.removeEventListener('keydown', this._handleKeyDown);
  }

  private _handleClickOutside(e: MouseEvent) {
    if (!this.contains(e.target as Node)) {
      this.open = false;
    }
  }

  private _handleKeyDown(e: KeyboardEvent) {
    if (!this.open) return;

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        this.open = false;
        this.trigger?.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        this._highlightNext();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._highlightPrevious();
        break;
      case 'Enter':
        e.preventDefault();
        if (this._highlightedIndex >= 0) {
          this._selectOption(this._filteredOptions[this._highlightedIndex]);
        }
        break;
    }
  }

  private _highlightNext() {
    const maxIndex = this._filteredOptions.length - 1;
    this._highlightedIndex = this._highlightedIndex < maxIndex ? this._highlightedIndex + 1 : 0;
  }

  private _highlightPrevious() {
    const maxIndex = this._filteredOptions.length - 1;
    this._highlightedIndex = this._highlightedIndex > 0 ? this._highlightedIndex - 1 : maxIndex;
  }

  private _toggleDropdown() {
    if (!this.disabled) {
      this.open = !this.open;
    }
  }

  private _selectOption(option: {value: string, label: string}) {
    if (this.multiple) {
      const index = this._selectedOptions.indexOf(option.value);
      if (index > -1) {
        this._selectedOptions.splice(index, 1);
      } else {
        this._selectedOptions.push(option.value);
      }
      this.value = [...this._selectedOptions];
    } else {
      this.value = option.value;
      this.open = false;
    }

    emit(this, 'ewc-change', { detail: { value: this.value } });
    this.reportValidity();
  }

  private _removeTag(value: string, e: Event) {
    e.stopPropagation();
    const index = this._selectedOptions.indexOf(value);
    if (index > -1) {
      this._selectedOptions.splice(index, 1);
      this.value = [...this._selectedOptions];
      emit(this, 'ewc-change', { detail: { value: this.value } });
    }
  }

  private _handleClear(e: Event) {
    e.stopPropagation();
    this.value = this.multiple ? [] : '';
    emit(this, 'ewc-clear');
    emit(this, 'ewc-change', { detail: { value: this.value } });
  }

  private _handleSearch(e: Event) {
    const target = e.target as HTMLInputElement;
    this._searchQuery = target.value.toLowerCase();
    this._filteredOptions = this.options.filter(option =>
      option.label.toLowerCase().includes(this._searchQuery)
    );
    this._highlightedIndex = -1;
  }

  private _focusSearch() {
    if (this.searchable && this.searchInput) {
      requestAnimationFrame(() => this.searchInput.focus());
    }
  }

  private _renderValue() {
    if (this.multiple) {
      if (this._selectedOptions.length === 0) {
        return html`<span class="select-value placeholder">${this.placeholder}</span>`;
      }

      const selectedLabels = this._selectedOptions.map(val => 
        this.options.find(opt => opt.value === val)?.label || val
      );

      if (this.maxOptionsVisible > 0 && selectedLabels.length > this.maxOptionsVisible) {
        const visibleTags = selectedLabels.slice(0, this.maxOptionsVisible);
        const moreCount = selectedLabels.length - this.maxOptionsVisible;
        
        return html`
          <div class="select-tags">
            ${visibleTags.map((label, index) => html`
              <span class="select-tag">
                ${label}
                <button
                  class="select-tag-remove"
                  @click=${(e: Event) => this._removeTag(this._selectedOptions[index], e)}
                  tabindex="-1"
                  aria-label="Remove ${label}"
                >
                  ×
                </button>
              </span>
            `)}
            <span class="select-tag-more">+${moreCount} more</span>
          </div>
        `;
      }

      return html`
        <div class="select-tags">
          ${selectedLabels.map((label, index) => html`
            <span class="select-tag">
              ${label}
              <button
                class="select-tag-remove"
                @click=${(e: Event) => this._removeTag(this._selectedOptions[index], e)}
                tabindex="-1"
                aria-label="Remove ${label}"
              >
                ×
              </button>
            </span>
          `)}
        </div>
      `;
    } else {
      const selectedOption = this.options.find(opt => opt.value === this.value);
      return selectedOption
        ? html`<span class="select-value">${selectedOption.label}</span>`
        : html`<span class="select-value placeholder">${this.placeholder}</span>`;
    }
  }

  render() {
    const hasValue = this.multiple ? this._selectedOptions.length > 0 : !!this.value;

    return html`
      <div class="select-wrapper">
        ${this.label ? html`
          <label class="label">
            ${this.label}${this.required ? html`<span style="color: var(--ewc-color-error);"> *</span>` : ''}
          </label>
        ` : ''}
        
        <button
          class="select-trigger"
          @click=${this._toggleDropdown}
          ?disabled=${this.disabled}
          type="button"
          role="combobox"
          style="--max-height: ${this.maxHeight}px"
        >
          ${this._renderValue()}
          
          <div class="select-icons">
            ${this.clearable && hasValue && !this.disabled ? html`
              <span class="select-clear" @click=${this._handleClear} role="button" tabindex="-1">
                ×
              </span>
            ` : ''}
            <span class="select-chevron">▼</span>
          </div>
        </button>
        
        <div class="select-dropdown" role="listbox" aria-multiselectable=${this.multiple}>
          ${this.searchable ? html`
            <div class="select-search-wrapper">
              <input
                class="select-search"
                type="text"
                placeholder="Search options..."
                .value=${this._searchQuery}
                @input=${this._handleSearch}
                tabindex=${this.open ? '0' : '-1'}
              />
            </div>
          ` : ''}
          
          <div class="select-options">
            ${this._filteredOptions.length === 0 ? html`
              <div class="select-empty">No options found</div>
            ` : this._filteredOptions.map((option, index) => {
              const isSelected = this._selectedOptions.includes(option.value);
              const isHighlighted = index === this._highlightedIndex;
              
              return html`
                <div
                  class="select-option ${classMap({
                    selected: isSelected,
                    highlighted: isHighlighted
                  })}"
                  @click=${() => this._selectOption(option)}
                  role="option"
                  aria-selected=${isSelected}
                  tabindex="-1"
                >
                  ${this.multiple ? html`
                    <span class="select-option-checkbox"></span>
                  ` : ''}
                  ${option.label}
                </div>
              `;
            })}
          </div>
        </div>
        
        ${this.error || this.validationMessage ? 
          html`<div class="error-text" role="alert">${this.error || this.validationMessage}</div>` : 
          this.helper ? 
            html`<div class="helper-text">${this.helper}</div>` : ''}
      </div>
    `;
  }

  // Public methods
  async show() {
    if (!this.open && !this.disabled) {
      this.open = true;
      await waitForEvent(this, 'ewc-after-show');
    }
  }

  async hide() {
    if (this.open) {
      this.open = false;
      await waitForEvent(this, 'ewc-after-hide');
    }
  }

  focus() {
    this.trigger?.focus();
  }

  blur() {
    this.trigger?.blur();
  }
}